/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useCallback, useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { PROJECT_TITLE } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { ProjectIcon } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import { ProjectActionMenu } from '@atlassian/jira-navigation-project-action-menu/src/ui/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { ProjectHeader_inner_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_project.graphql';
import type { ProjectHeader_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_query.graphql';
import type { ProjectHeader_project$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_project.graphql';
import type { ProjectHeader_inner_query$key } from '@atlassian/jira-relay/src/__generated__/ProjectHeader_inner_query.graphql';
import BaseHeader, {
	type WithInlineEditProps,
} from '@atlassian/jira-horizontal-nav-header/src/common/ui/BaseHeader.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import isWithinLast7Days from '@atlassian/jira-onboarding/src/utils/isWithinLast7Days.tsx';
import isWithinLastNDays from '@atlassian/jira-onboarding/src/utils/isWithinLastNDays.tsx';

import { useIntentionalHover } from '@atlassian/jira-onboarding/src/hooks/use-intentional-hover/use-intentional-hover.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NavigationInviteButton } from '../invite-button/NavigationInviteButton.tsx';
import { ExtraComponentsFar } from '../extra-components-far/ExtraComponentsFar.tsx';
import { useRenameProject } from '../../../../services/rename-project/useRenameProject.tsx';
import { HorizontalNavProjectBreadcrumbs } from '../breadcrumbs/HorizontalNavProjectBreadcrumbs.tsx';
import { NavigationTeamButton } from '../team-button/NavigationTeamButton.tsx';
import ProjectAvatar from './project-avatar/ProjectAvatar.tsx';

export const ProjectHeader = (props: {
	queryFragment: ProjectHeader_query$key;
	projectFragment: ProjectHeader_project$key;
}): React.JSX.Element => {
	const { queryFragment, projectFragment } = props;
	const data = useFragment(
		graphql`
			fragment ProjectHeader_query on Query {
				...ProjectHeader_inner_query
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_project on JiraProject {
				...ProjectHeader_inner_project
			}
		`,
		projectFragment,
	);

	const analyticsData = useMemo(
		() => ({
			navigationContainer: PROJECT_TITLE,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<ProjectHeaderInner {...props} queryFragment={data} projectFragment={project} />
		</ContextualAnalyticsData>
	);
};

const ProjectHeaderInner = ({
	queryFragment,
	projectFragment,
}: {
	queryFragment: ProjectHeader_inner_query$key;
	projectFragment: ProjectHeader_inner_project$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isFullscreen] = useIsFullscreen();
	const { updateProjectName, validateProjectName } = useRenameProject();

	const data = useFragment(
		graphql`
			fragment ProjectHeader_inner_query on Query {
				jira @required(action: THROW) {
					jwmLicensing(cloudId: $cloudId) {
						currentUserSeatEdition
					}
				}
			}
		`,
		queryFragment,
	);

	const project = useFragment(
		graphql`
			fragment ProjectHeader_inner_project on JiraProject
			@argumentDefinitions(
				showInviteButton: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-invite-button.relayprovider"
				}
			) {
				...ProjectAvatar
				...NavigationTeamButton_project
				...NavigationInviteButton_project @include(if: $showInviteButton)
				name
				key @required(action: THROW)
				projectType @required(action: THROW)
				projectId
				avatar {
					large
				}
				canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
					canPerform
				}
				created @include(if: $showInviteButton)
				...ExtraComponentsFar
				...HorizontalNavProjectBreadcrumbs
			}
		`,
		projectFragment,
	);

	const isBusinessProject = project.projectType === 'BUSINESS';
	const now = new Date();
	const canEditProjectConfig = project?.canEditProjectConfig?.canPerform ?? false;

	/**
	 * Determines if a project is eligible for the inline title and icon editing experiment
	 * Eligibility criteria:
	 * - Project has a creation date
	 * - User has project configuration edit permissions
	 * - Project is NOT a business project (business project already has this functionality)
	 * - Project was created within the last 14 days
	 * - User is enrolled in treatment of 'jfind-make-project-name-editable-experiment'
	 */
	const isEligibleForEditProjectTitleTreatment =
		project?.created &&
		canEditProjectConfig &&
		!isBusinessProject &&
		isWithinLastNDays(project.created, 14, now) &&
		expVal('jfind-make-project-name-editable-experiment', 'enrolled', false);

	const withInlineEdit = isBusinessProject || isEligibleForEditProjectTitleTreatment;
	const isCorePremiumUserSeat = data.jira?.jwmLicensing?.currentUserSeatEdition === 'PREMIUM';

	const isAnalyticsEnabled = canEditProjectConfig && !withInlineEdit;

	const fireAnalytics = useCallback(
		(action: string, actionSubject: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: 'editProjectConfig',
					source: 'projectHeader',
					attributes: {
						projectType: project.projectType || 'unknown',
						projectId: project.projectId,
					},
				},
			});
		},
		[createAnalyticsEvent, project.projectId, project.projectType],
	);

	const handleIconIntentionalHover = useCallback(() => {
		fireAnalytics('hovered', 'projectIcon');
	}, [fireAnalytics]);

	const { onMouseEnter, onMouseLeave } = useIntentionalHover(() => {
		handleIconIntentionalHover();
	}, 500);

	const handleIconClick = useCallback(
		() => fireAnalytics('clicked', 'projectIcon'),
		[fireAnalytics],
	);

	const icon = useMemo(() => {
		const avatar = (
			<Box xcss={iconStyles}>
				<ProjectAvatar projectRef={project} withEditMode={!!withInlineEdit} />
			</Box>
		);

		if (withInlineEdit) {
			return avatar;
		}

		if (!project?.avatar?.large) {
			return null;
		}

		return fg('jfind-make-project-name-editable-in-nav4-gate') ? (
			// Analytics: Measuring user demand for project icon editing feature
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				xcss={noLayoutShiftStyles}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onClick={handleIconClick}
			>
				<ProjectIcon
					data-testid="horizontal-nav-header.ui.project-header.project-icon"
					data-vc="horizontal-nav-project-icon"
					src={project.avatar.large}
					alt=""
				/>
			</Box>
		) : (
			<ProjectIcon
				data-testid="horizontal-nav-header.ui.project-header.project-icon"
				data-vc="horizontal-nav-project-icon"
				src={project.avatar.large}
				alt=""
			/>
		);
	}, [handleIconClick, onMouseEnter, onMouseLeave, project, withInlineEdit]);

	const handleProjectActionDropDownClick = useCallback(
		() => fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'projectActionsDropdown', {}),
		[createAnalyticsEvent],
	);

	const onUpdateProjectName = useCallback(
		(name: string) => updateProjectName(Number(project?.projectId), name),
		[project?.projectId, updateProjectName],
	);

	let headerProps = {};
	if (withInlineEdit && project?.projectId) {
		const inlineEditProps: WithInlineEditProps = {
			withInlineEdit: true,
			onConfirm: onUpdateProjectName,
			onValidate: validateProjectName,
			id: Number(project.projectId),
		};

		headerProps = canEditProjectConfig ? inlineEditProps : {};
	}

	const showInviteButton =
		project?.created &&
		canEditProjectConfig &&
		project.projectId === '10000' && // only setting invite button for the default project
		isWithinLast7Days(project.created, now) &&
		expVal('jfind-restore-invite-button-experiment-2', 'enrolled', false);

	const extraComponentsClose = useMemo((): ReactNode => {
		if (!project) {
			return null;
		}

		return (
			<>
				{showInviteButton && <NavigationInviteButton projectFragment={project} scope="project" />}
				{!showInviteButton && <NavigationTeamButton projectFragment={project} scope="project" />}
				<ProjectActionMenu
					data-testid="horizontal-nav-header.ui.project-header.project-action-menu"
					key="ProjectActions"
					projectKey={project.key}
					isCorePremiumUserSeat={isCorePremiumUserSeat}
					withThemedTrigger
					onClick={handleProjectActionDropDownClick}
					shouldRenderToParent
				/>
			</>
		);
	}, [project, showInviteButton, isCorePremiumUserSeat, handleProjectActionDropDownClick]);

	if (!project) {
		return null;
	}

	return (
		<BaseHeader
			title={project.name}
			{...headerProps}
			icon={icon}
			isLoading={project == null}
			extraComponentsClose={extraComponentsClose}
			testId="horizontal-nav-header.ui.project-header.header"
			extraComponentsFar={<ExtraComponentsFar projectFragment={project} />}
			extraComponentsAbove={
				!isFullscreen && <HorizontalNavProjectBreadcrumbs projectFragment={project} />
			}
			fireAnalytics={
				isAnalyticsEnabled && fg('jfind-make-project-name-editable-in-nav4-gate')
					? fireAnalytics
					: undefined
			}
		/>
	);
};

const iconStyles = xcss({
	width: '20px',
	height: '20px',
	boxSizing: 'border-box',
});

const noLayoutShiftStyles = xcss({
	display: 'contents',
});
